import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { LoadingState } from '@/components/ui/states'
import { usePublicEvent } from '@/hooks/usePublicSchedule'
import { PublicShell, PublicProblem } from '@/components/public/PublicShell'
import { PlanPhase } from '@/components/public/PlanPhase'
import { formatEventDate, timeRangeLabel } from '@/lib/eventTime'

/**
 * One event, for whoever was sent this link.
 *
 * The date is at the top rather than only on the list it came from: a VM may
 * paste this page's URL on its own, and a crew opening the second of two links
 * has to be able to tell which day it is.
 */
export function PublicEventPage(): React.ReactElement {
  const { token, eventId } = useParams()
  const shareToken = token ?? ''
  const query = usePublicEvent(shareToken, eventId ?? '')

  if (query.isPending) {
    return (
      <PublicShell>
        <LoadingState label="Loading…" />
      </PublicShell>
    )
  }

  if (query.isError) return <PublicProblem error={query.error} />

  const event = query.data
  const times = timeRangeLabel(event.startsAt, event.endsAt)

  return (
    <PublicShell venueName={event.venueName}>
      <Link
        to={`/s/${shareToken}`}
        data-print-hide
        className="inline-flex items-center gap-1 text-sm text-ink-muted outline-none transition-colors hover:text-ink focus-visible:focus-ds"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> All dates
      </Link>

      <div className="space-y-1">
        <h1 className="m-0 text-2xl font-semibold text-ink">{formatEventDate(event.date)}</h1>
        {times === '' ? null : <p className="m-0 font-mono text-sm tabular-nums text-ink-muted">{times}</p>}
      </div>

      {event.phases.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">
          Nothing has been planned for this date yet. Check back, or ask the venue.
        </p>
      ) : (
        <div className="space-y-10">
          {event.phases.map((phase) => (
            <PlanPhase key={phase.id} phase={phase} />
          ))}
        </div>
      )}
    </PublicShell>
  )
}
